'use client';

import React from 'react';
import { Typography, Button, Picture } from '@workspace/ui/common';
import { typographyVariants } from '@workspace/ui/common/Typography/typography.variants';
import type { MiddleBannerProps } from './middle-banner.types';
import { processHeading, mapHeadingVariant } from '@workspace/lib/heading-utils';
import { cn, getBackgroundClass, getBackgroundStyle, getButtonVariant, shouldOpenInNewTab } from '@workspace/lib';
import { stripInvisibles } from '@workspace/constants';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { middleBannerVariants, shouldUseLightText } from '@workspace/blocks/MiddleBanner/middleBanner.variants';
import { normalizeAlignment } from './middleBanner.variants.defaults';

export function MiddleBanner({ block }: MiddleBannerProps) {
  const {
    isActive = true,
    backgroundType = 'color',
    backgroundColor,
    backgroundImage,
    textColor,
    heading,
    headingAlignment = 'text-center',
    subheading,
    subheadingAlignment = 'text-center',
    bodyContent,
    bodyAlignment = 'text-center',
    buttonOptional,
  } = block;

  if (isActive === false) return null;

  const { text: headingText } = processHeading(heading);
  const headingVariant = mapHeadingVariant(heading, 'h2');
  const headingFontClass = typographyVariants({ variant: headingVariant });

  const isImageBg = backgroundType === 'image';
  const bgImageTarget = Array.isArray(backgroundImage) ? backgroundImage[0] : backgroundImage;
  const hasImage = Boolean(
    typeof bgImageTarget === 'string'
      ? bgImageTarget
      : bgImageTarget?.selectedUrl ||
          bgImageTarget?.url ||
          bgImageTarget?.datUrl ||
          bgImageTarget?.previewUrl ||
          bgImageTarget?.asset?.url,
  );
  const imageProps = typeof bgImageTarget === 'string' ? { src: bgImageTarget } : { image: bgImageTarget };

  const backgroundClass = isImageBg ? '' : getBackgroundClass(backgroundColor);
  const hasLightText = shouldUseLightText({
    backgroundClass,
    isImageBg,
    textColor,
  });

  const backgroundStyle: React.CSSProperties | undefined = isImageBg
    ? undefined
    : backgroundClass
      ? undefined
      : getBackgroundStyle(backgroundColor);

  const {
    section,
    container,
    content,
    heading: headingSlot,
    subheading: subheadingSlot,
    body: bodySlot,
    buttonWrapper,
    button: buttonSlot,
  } = middleBannerVariants({
    hasLightText,
    headingAlignment: normalizeAlignment(headingAlignment),
    subheadingAlignment: normalizeAlignment(subheadingAlignment),
    bodyAlignment: normalizeAlignment(bodyAlignment),
  });

  const buttonLink = buttonOptional?.linkOptional ?? (buttonOptional as any)?.link;
  const buttonLabel = stripInvisibles(buttonOptional?.label || '').trim();
  const showButton = Boolean(buttonOptional?.isActive && buttonLabel && (buttonLink?.type || buttonLink?.url));

  return (
    <div
      className={cn(
        'background-container w-full relative overflow-hidden flex items-center',
        backgroundClass,
        section(),
      )}
      style={backgroundStyle}
    >
      {isImageBg && hasImage && (
        <>
          <Picture
            {...imageProps}
            desktopWidth={1920}
            desktopHeight={600}
            tabletWidth={1024}
            tabletHeight={500}
            mobileWidth={390}
            mobileHeight={600}
            fill
            className="absolute inset-0 size-full"
            imgClassName="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        </>
      )}

      <div className="porta-container w-full relative z-10">
        <div className={cn('w-full flex flex-col justify-center mx-auto', container())}>
          <div className={cn('text-container w-full flex flex-col items-center', content())}>
            {headingText && (
              <Typography variant="h2" className={cn('w-full', headingFontClass, headingSlot())}>
                {headingText}
              </Typography>
            )}
            {subheading && (
              <Typography variant="h3" className={cn('w-full', subheadingSlot())} styleVariant="h5">
                {subheading}
              </Typography>
            )}
          </div>

          {bodyContent && bodyContent.length > 0 && (
            <div className={bodySlot()}>
              <CustomPortableText value={bodyContent} className="prose prose-lg max-w-none" />
            </div>
          )}

          {showButton && (
            <div className={cn('flex w-full justify-center', buttonWrapper())}>
              <Button
                variant={getButtonVariant(buttonOptional?.style)}
                url={buttonLink?.url || '#'}
                openInNewTab={shouldOpenInNewTab(buttonLink)}
                className={buttonSlot()}
              >
                <span className="flex-1 text-center">{buttonLabel}</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
